import { useState, useRef } from "react";
import { Plus, Minus } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const faqs = [
  {
    question: "O que é o AFRMM e por que minha empresa tem direito?",
    answer:
      "O AFRMM (Adicional ao Frete para Renovação da Marinha Mercante) é uma contribuição cobrada sobre fretes marítimos em portos brasileiros. Os recursos são destinados ao Fundo da Marinha Mercante (FMM) e podem ser utilizados por Empresas Brasileiras de Navegação (EBNs) para investimento em frota e construção naval. Com a Lei BR do Mar (14.301/2022), novas categorias de empresas passaram a ter direito — e muitas ainda não sabem.",
  },
  {
    question: "Como funciona o processo de liberação dos recursos?",
    answer:
      "O processo passa por etapas: diagnóstico, auditoria do Sistema Mercante, estruturação regulatória, elaboração e submissão via COMPROT, acompanhamento junto à RFB e BNDES, e liberação final. Cada etapa tem exigências que, se não atendidas, travam tudo. A SIATECH cuida de cada uma delas.",
  },
  {
    question: "Quanto tempo demora o processo?",
    answer:
      "Com nossa tecnologia, o que levava mais de 1 dia por operação é processado em minutos. O prazo total varia conforme a complexidade de cada empresa — mas garantimos acompanhamento ativo e atuação proativa para evitar atrasos.",
  },
  {
    question: "Quais empresas podem solicitar?",
    answer:
      "EBNs que operam em Navegação Interior no Norte e Nordeste ou em Navegação de Cabotagem na costa brasileira. Desde a BR do Mar, o universo de empresas elegíveis se expandiu significativamente.",
  },
  {
    question: "Como posso saber se minha empresa tem direito?",
    answer:
      "É exatamente a primeira etapa — e é gratuita. Em uma conversa rápida, nossa equipe mapeia sua operação e confirma elegibilidade, potencial e o melhor caminho. Sem compromisso.",
  },
  {
    question: "Já solicito AFRMM. Por que precisaria da SIATECH?",
    answer:
      "Muitas empresas operam de forma manual, com risco de erros e muito tempo investido. Nossa tecnologia automatiza etapas repetitivas, elimina erros e libera sua equipe para o estratégico.",
  },
  {
    question: "O processo é sigiloso?",
    answer:
      "Sim. Todos os dados são tratados com total confidencialidade, em conformidade com a LGPD.",
  },
  {
    question: "Preciso fornecer acesso ao Sistema Mercante?",
    answer:
      "Sim. Todo o acesso é documentado e realizado com sua supervisão e autorização.",
  },
];

const AccordionItem = ({
  faq,
  isOpen,
  onToggle,
}: {
  faq: { question: string; answer: string };
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div style={{ borderBottom: "1px solid var(--color-cinza-nevoa)" }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left cursor-pointer"
        aria-expanded={isOpen}
      >
        <span
          className="font-medium text-[15px] pr-4"
          style={{ color: "var(--color-preto-marca)" }}
        >
          {faq.question}
        </span>
        <span
          className="flex-shrink-0 transition-colors duration-200"
          style={{ color: isOpen ? "var(--color-dourado)" : "var(--color-cinza-ancora)" }}
        >
          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? `${contentRef.current?.scrollHeight ?? 500}px` : "0px",
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className="pb-5">
          <p
            className="text-[14px] font-light leading-relaxed"
            style={{ color: "var(--color-cinza-ancora)", lineHeight: 1.7 }}
          >
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const sectionRef = useScrollReveal();

  return (
    <section
      id="faq"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-dots"
      style={{ background: "var(--color-branco-vela)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-animate>
          <div className="eyebrow justify-center">FAQ</div>
          <h2
            className="heading-h2 mb-4"
            style={{ color: "var(--color-preto-marca)" }}
          >
            Perguntas que toda empresa faz antes de começar.
          </h2>
          <div
            className="w-14 h-px mx-auto"
            style={{ background: "var(--color-cinza-nevoa)" }}
          />
        </div>

        {/* Accordion */}
        <div className="max-w-3xl mx-auto mb-12" data-animate>
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>

        {/* CTA below FAQ */}
        <div className="max-w-2xl mx-auto" data-animate>
          <div
            className="p-8 text-center rounded"
            style={{
              background: "white",
              border: "1px solid var(--color-cinza-nevoa)",
            }}
          >
            <p
              className="text-base font-light mb-4"
              style={{ color: "var(--color-preto-marca)" }}
            >
              Ainda tem dúvidas? Fale diretamente com um especialista SIATECH —
              sem compromisso.
            </p>
            <a href="#contato" className="btn-outline-light inline-flex">
              Falar com um Especialista →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
